import { MedicineBoxOutlined, ToolOutlined } from '@ant-design/icons'
import { Col, Form, Input, Row, Select, Spin, Tag, message } from 'antd'
import { debounce } from 'lodash'
import React, { useEffect, useState } from 'react'
import { MedPraxProductSearch } from '../../../../../MedPrax/ProductsAPI'
import { MedPraxMaterialSearch } from '../../../../../Scripting/utils/utils'
import { VALIDATION_RULES } from '../../utils/constants'
import { mockCategories } from '../../utils/mockInventoryData'

const { Option } = Select
const { TextArea } = Input

// Helper function to convert text to Title Case
const toTitleCase = (str) => {
  if (!str) return ''
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const BasicInformationSection = ({
  form,
  isSearching,
  setIsSearching,
  searchResults,
  setSearchResults,
  inventoryTrackingMethod,
  setInventoryTrackingMethod,
  isTrackedByPacks,
  setIsTrackedByPacks,
  item,
  setMedpraxPrice,
  styles
}) => {
  // Track the selected product to keep it in the search field
  const [selectedProductId, setSelectedProductId] = useState(null)

  // Reset selected product when search results are cleared (modal reset)
  useEffect(() => {
    if (searchResults.length === 0) {
      setSelectedProductId(null)
    }
  }, [searchResults])

  // Search for products in MedPrax
  const searchMedPraxProducts = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 3) {
      return // Don't clear results if search term is too short
    }

    setIsSearching(true)

    try {
      // Use the real APIs with proper error handling for both
      const [productsResults, materialsResults] = await Promise.allSettled([
        MedPraxProductSearch(searchTerm),
        MedPraxMaterialSearch(searchTerm)
      ])

      // Process results from both APIs
      const medications =
        productsResults.status === 'fulfilled'
          ? (productsResults.value || []).map((item) => {
              // Ensure price is properly parsed as a number
              let price = 0
              try {
                // Log all available price-related fields

                // Try SEP field first, then fall back to regular price
                if (item.SEP !== undefined) {
                  const sepStr = String(item.SEP).replace(/[^\d.-]/g, '')
                  price = parseFloat(sepStr)
                } else if (item.sep !== undefined) {
                  const sepStr = String(item.sep).replace(/[^\d.-]/g, '')
                  price = parseFloat(sepStr)
                } else if (item.singleExitPrice !== undefined) {
                  const sepStr = String(item.singleExitPrice).replace(/[^\d.-]/g, '')
                  price = parseFloat(sepStr)
                } else if (item.price !== undefined) {
                  // Handle different price formats (string with commas, currency symbols, etc.)
                  const priceStr = String(item.price).replace(/[^\d.-]/g, '')
                  price = parseFloat(priceStr)
                }
              } catch (e) {
                console.error('Error parsing price:', e)
              }

              return {
                ...item,
                id: `med-${item.code || item.id}`,
                itemType: 'Medication',
                code: item.code || '',
                name: toTitleCase(item.name || item.description || ''),
                description: toTitleCase(item.description || item.name || ''),
                nappiCode: item.nappiCode || item.NappiCode || '',
                standardPacksize: parseInt(item.standardPacksize || item.packSize || 1, 10),
                unitOfMeasure: item.unitOfMeasure1 || item.unitOfMeasure || '',
                price: isNaN(price) ? 0 : price, // Ensure price is a valid number
                category: 'Medication',
                strength: item.strengthMetric1 || '',
                manufacturer: toTitleCase(item.manufacturer || ''),
                dosageForm: toTitleCase(item.dosageForm || ''),
                atcCode: item.atcCode || '',
                batchNo: item.batchNo || ''
              }
            })
          : []

      const materials =
        materialsResults.status === 'fulfilled'
          ? (materialsResults.value || []).map((item) => {
              // Ensure price is properly parsed as a number
              let price = 0
              try {
                // Log all available price-related fields

                // Try SEP field first, then fall back to regular price
                if (item.SEP !== undefined) {
                  const sepStr = String(item.SEP).replace(/[^\d.-]/g, '')
                  price = parseFloat(sepStr)
                } else if (item.sep !== undefined) {
                  const sepStr = String(item.sep).replace(/[^\d.-]/g, '')
                  price = parseFloat(sepStr)
                } else if (item.singleExitPrice !== undefined) {
                  const sepStr = String(item.singleExitPrice).replace(/[^\d.-]/g, '')
                  price = parseFloat(sepStr)
                } else if (item.price !== undefined) {
                  // Handle different price formats (string with commas, currency symbols, etc.)
                  const priceStr = String(item.price).replace(/[^\d.-]/g, '')
                  price = parseFloat(priceStr)
                }
              } catch (e) {
                console.error('Error parsing price:', e)
              }

              return {
                ...item,
                id: `mat-${item.code || item.id}`,
                itemType: 'Material',
                code: item.code || '',
                name: toTitleCase(item.name || item.description || ''),
                description: toTitleCase(item.description || item.name || ''),
                nappiCode: item.nappiCode || item.NappiCode || '',
                standardPacksize: parseInt(item.standardPacksize || item.packSize || 1, 10),
                unitOfMeasure: item.unitOfMeasure1 || item.unitOfMeasure || '',
                price: isNaN(price) ? 0 : price, // Ensure price is a valid number
                category: 'Material',
                strength: '',
                manufacturer: toTitleCase(item.manufacturer || ''),
                batchNo: item.batchNo || ''
              }
            })
          : []

      // Combine and sort results
      const combinedResults = [...medications, ...materials].sort((a, b) => {
        // Sort by name alphabetically
        return a.name.localeCompare(b.name)
      })

      setSearchResults(combinedResults)

      // Log search results count for debugging

      if (combinedResults.length === 0) {
        message.info('No matching products found in the MedPrax database.')
      }
    } catch (error) {
      console.error('Error searching products:', error)
      message.error('Error searching products: ' + error.message)
    } finally {
      setIsSearching(false)
    }
  }

  // Fill form with selected product from search
  const selectSearchResult = (value) => {
    const product = searchResults.find((p) => p.id === value)
    if (!product) return

    setSelectedProductId(value)

    // Log the entire product object to check all fields

    // Get packSize value, default to 1 if not available
    const packSize = product.standardPacksize || 1

    // Always set tracking method to wholePacksOnly, regardless of product type
    setInventoryTrackingMethod('wholePacksOnly')
    setIsTrackedByPacks(packSize > 1)

    // Format the product name to include strength/details if available
    let fullProductName = product.name
    if (product.strength && product.itemType === 'Medication') {
      fullProductName = `${product.name} ${product.strength}`
    }
    if (product.unitOfMeasure) {
      fullProductName = `${fullProductName} ${product.unitOfMeasure}`
    }

    // Build a comprehensive description
    let fullDescription = ''

    // Start with the base description if available
    if (product.description) {
      fullDescription = product.description
    } else {
      fullDescription = product.name // Fallback to name if no description
    }

    // For medications, add therapeutic information
    if (product.itemType === 'Medication') {
      // Add strength if available and not already in description
      if (product.strength && !fullDescription.includes(product.strength)) {
        fullDescription += `\nStrength: ${product.strength}`
      }

      // Add dosage form if available
      if (product.dosageForm && !fullDescription.includes(product.dosageForm)) {
        fullDescription += `\nForm: ${product.dosageForm}`
      }

      // Add ATC code if available
      if (product.atcCode) {
        fullDescription += `\nATC Code: ${product.atcCode}`
      }
    }

    // Add manufacturer information
    if (product.manufacturer) {
      fullDescription += `\nManufacturer: ${product.manufacturer}`
    }

    // Add pack information
    if (packSize > 1) {
      fullDescription += `\nPack Size: ${packSize} units per pack`
    }

    // Add unit information if available
    if (product.unitOfMeasure && !fullDescription.includes(product.unitOfMeasure)) {
      fullDescription += `\nUnit: ${product.unitOfMeasure}`
    }

    // Ensure price is a valid number
    let unitCost = 0

    // Try different price fields in order of preference
    if (product.SEP !== undefined && product.SEP !== null) {
      unitCost = parseFloat(String(product.SEP).replace(/[^\d.-]/g, '')) || 0
    } else if (product.sep !== undefined && product.sep !== null) {
      unitCost = parseFloat(String(product.sep).replace(/[^\d.-]/g, '')) || 0
    } else if (product.singleExitPrice !== undefined && product.singleExitPrice !== null) {
      unitCost = parseFloat(String(product.singleExitPrice).replace(/[^\d.-]/g, '')) || 0
    } else if (product.costPrice !== undefined && product.costPrice !== null) {
      unitCost = parseFloat(String(product.costPrice).replace(/[^\d.-]/g, '')) || 0
    } else if (product.price !== undefined && product.price !== null) {
      unitCost = parseFloat(String(product.price).replace(/[^\d.-]/g, '')) || 0
    } else {
    }

    // Force update unitCost specifically
    form.setFieldValue('unitCost', unitCost)

    // Notify parent component about the price update
    if (setMedpraxPrice) {
      setMedpraxPrice(unitCost)
    }

    // Combine all available data for the form
    form.setFieldsValue({
      name: fullProductName,
      description: fullDescription,
      category: product.itemType === 'Medication' ? 'Medication' : 'Material',
      unitCost: unitCost, // Make sure price is set as a number
      nappiCode: product.nappiCode,
      // Adding more fields from the API response
      packSize: packSize,
      lotNumber: product.batchNo || '',
      // Let the user fill in the quantity on hand
      quantityOnHand: 0,
      isTrackedByPacks: packSize > 1,
      inventoryTrackingMethod: 'wholePacksOnly',
      partialUnitsUsed: 0,
      // Additional fields if they exist in the form
      ...(product.manufacturer && { manufacturer: product.manufacturer }),
      ...(product.dosageForm && { dosageForm: product.dosageForm }),
      ...(product.atcCode && { atcCode: product.atcCode })
    })

    // After setting form values, log what was actually set
  }

  // Handle clear selection
  const handleClearSelection = () => {
    setSelectedProductId(null)
  }

  return (
    <>
      <div className={styles.sectionHeader}>Basic Information</div>

      {/* Only show the search bar when adding a new item (not when editing) */}
      {!item && (
        <Row gutter={24} style={{ marginBottom: 16 }}>
          <Col span={24}>
            <Form.Item label='Search MedPrax Products' className={styles.field}>
              <Select
                showSearch
                placeholder='Search for products by name or NAPPI code (min 3 characters)'
                filterOption={false}
                onSearch={debounce(searchMedPraxProducts, 500)}
                onSelect={selectSearchResult}
                loading={isSearching}
                notFoundContent={isSearching ? <Spin size='small' /> : 'No products found'}
                optionLabelProp='label'
                listHeight={400}
                style={{ width: '100%' }}
                dropdownStyle={{ width: '100%', maxWidth: '100%' }}
                dropdownMatchSelectWidth={false}
                getPopupContainer={(trigger) => trigger.parentNode}
                allowClear
                value={selectedProductId}
                onClear={handleClearSelection}
              >
                {searchResults.map((product) => (
                  <Option key={product.id} value={product.id} label={product.name}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>
                        {product.itemType === 'Medication' ? (
                          <MedicineBoxOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                        ) : (
                          <ToolOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                        )}
                        {product.name}
                        {product.strength && <span style={{ color: '#666' }}> {product.strength}</span>}
                      </span>
                      <Tag color={product.itemType === 'Medication' ? 'blue' : 'green'}>{product.itemType}</Tag>
                    </div>
                    <div style={{ color: '#666', fontSize: '12px', display: 'flex', justifyContent: 'space-between' }}>
                      {product.nappiCode && <span>NAPPI: {product.nappiCode}</span>}
                      {product.manufacturer && <span>Mfr: {product.manufacturer}</span>}
                      {product.standardPacksize > 1 && <span>Pack: {product.standardPacksize} units</span>}
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      )}

      <Row gutter={24}>
        <Col span={8}>
          <Form.Item
            name='name'
            label='Item Name'
            rules={[VALIDATION_RULES.required]}
            className={`${styles.field} ${styles.fieldRequired}`}
          >
            <Input placeholder='Enter item name' />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name='category'
            label='Category'
            rules={[VALIDATION_RULES.required]}
            className={`${styles.field} ${styles.fieldRequired}`}
          >
            <Select placeholder='Select category'>
              {mockCategories.map((category) => (
                <Option key={category.id} value={category.name}>
                  {category.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name='nappiCode' label='NAPPI Code' className={styles.field}>
            <Input placeholder='Enter NAPPI code' />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={24}>
          <Form.Item name='description' label='Description' className={styles.field}>
            <TextArea rows={5} placeholder='Enter item description' />
          </Form.Item>
        </Col>
      </Row>
    </>
  )
}

export default BasicInformationSection
